import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Box, Button, Checkbox, FormControlLabel, Grid, IconButton, ListItemText, TextField, Typography } from '@material-ui/core';
import { Add, Close } from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';
import { ConfigFieldControl, ConfigTextField, CoreEvent } from '@sierralabs/core-ui';
import { getEntityProperties } from '@sierralabs/entity-core';
import { useEntityContext } from '@sierralabs/entity-ui';
import { useQueryContext } from '@sierralabs/query-ui';
import SiteRouteAndParamPicker, { updateRouteAndParam } from '../../common/pickers/SiteRouteAndParamPicker';
import { AssignEntityDialog } from '../../dialogs/AssignEntityDialog';
import { useConfigComponent } from '../../hooks/component/useConfigComponent';
import { useSubscribeComponentChanges } from '../../hooks/component/useSubscribeComponentChanges';
import { useSiteContext } from '../../SiteContext';
import { SiteComponent } from '../SiteComponentPlugin';
import { AssignEntityEvent, AssignEntityWarning, useAssignEntityComponent } from './AssignEntityPlugin';
function isDeepSelected(component, id) {
    return (component.id === id ||
        (component.children || []).some(child => isDeepSelected(child, id)));
}
export const SiteAssignEntityDialog = props => {
    var _a, _b, _c;
    const component = useSubscribeComponentChanges(AssignEntityDialogPlugin, props);
    const { editing, selectedComponentRef } = useSiteContext();
    const assignEntity = useAssignEntityComponent(component);
    const [open, setOpen] = useState(false);
    const { assign, multiple, cardTableField, dialogTableField } = component.props || {};
    // use the table props from children
    const tableComponent = (_a = component.children) === null || _a === void 0 ? void 0 : _a.find(c => c.slot === 'table');
    const table = tableComponent === null || tableComponent === void 0 ? void 0 : tableComponent.props;
    const queryId = (_b = tableComponent === null || tableComponent === void 0 ? void 0 : tableComponent.queryIds) === null || _b === void 0 ? void 0 : _b[0];
    // reveal dialog when component or descendant is selected during edit mode
    const isOpen = open ||
        !!(editing &&
            selectedComponentRef.current &&
            isDeepSelected(component, selectedComponentRef.current.id));
    // handle open dialog event
    useEffect(() => {
        return CoreEvent.handle(AssignEntityEvent.OpenDialog, data => {
            if (data && (assignEntity === null || assignEntity === void 0 ? void 0 : assignEntity.id) && data.id === assignEntity.id) {
                setOpen(true);
            }
        });
    }, [assignEntity === null || assignEntity === void 0 ? void 0 : assignEntity.id]);
    return (React.createElement(SiteComponent, Object.assign({ plugin: AssignEntityDialogPlugin }, props),
        React.createElement(AssignEntityDialog, { open: isOpen, multiple: multiple, component: component, tableComponent: tableComponent, title: (_c = component.props) === null || _c === void 0 ? void 0 : _c.title, cardTableField: cardTableField, dialogTableField: dialogTableField, assignConfig: { queryId, table, props: assign }, onCancel: () => setOpen(false), onAssign: () => {
                setOpen(false);
                const id = assignEntity === null || assignEntity === void 0 ? void 0 : assignEntity.id;
                id && CoreEvent.dispatch(AssignEntityEvent.Assign, { id });
            } })));
};
const StyledGrid = styled(Grid) `
  .grid-field {
    flex: 1;
  }
  .grid-action {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 4px;
  }
`;
function getFields(queries, queryId) {
    var _a;
    const query = queryId
        ? queries.find(query => query.id === queryId)
        : undefined;
    return (((_a = query === null || query === void 0 ? void 0 : query.config) === null || _a === void 0 ? void 0 : _a.selects) || []).map(select => select.selection);
}
export const AssignEntityDialogConfigurator = props => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
    const { component, insert, update } = useConfigComponent(props.component);
    const assignEntity = useAssignEntityComponent(component);
    const { relationTarget } = (assignEntity === null || assignEntity === void 0 ? void 0 : assignEntity.props) || {};
    const assign = ((_a = component.props) === null || _a === void 0 ? void 0 : _a.assign) || {};
    const { queries } = useQueryContext();
    const { entities = [] } = useEntityContext();
    // route
    const selectedEntity = assign.tableName
        ? entities.find(entity => entity.tableName === assign.tableName)
        : undefined;
    // relations
    // handle options for matching ids for 2 queries (dialog table, card table)
    // get dialog table query from children
    const dialogTableFields = getFields(queries, (_d = (_c = (_b = component.children) === null || _b === void 0 ? void 0 : _b.find(c => c.slot === 'table')) === null || _c === void 0 ? void 0 : _c.queryIds) === null || _d === void 0 ? void 0 : _d[0]);
    // get card table query from assignEntity -> card -> table
    const cardTableFields = getFields(queries, (_j = (_h = (_g = (_f = (_e = assignEntity === null || assignEntity === void 0 ? void 0 : assignEntity.children) === null || _e === void 0 ? void 0 : _e.find(c => c.slot === 'card')) === null || _f === void 0 ? void 0 : _f.children) === null || _g === void 0 ? void 0 : _g.find(c => c.slot === 'table')) === null || _h === void 0 ? void 0 : _h.queryIds) === null || _j === void 0 ? void 0 : _j[0]);
    const properties = getEntityProperties(assign.relations || [], selectedEntity, entities);
    return (React.createElement(React.Fragment, null,
        React.createElement(AssignEntityWarning, { component: component }),
        React.createElement(ConfigFieldControl, null,
            React.createElement(ConfigTextField, { fullWidth: true, label: 'Dialog Title', value: (_k = component.props) === null || _k === void 0 ? void 0 : _k.title, onChange: event => update('props.title', event.target.value) })),
        React.createElement(ConfigFieldControl, null,
            React.createElement(FormControlLabel, { label: 'Multiple Select', control: React.createElement(Checkbox, { size: 'small', color: 'primary', className: 'checkbox', checked: !!((_l = component.props) === null || _l === void 0 ? void 0 : _l.multiple), onChange: () => {
                        var _a;
                        update('props.multiple', !((_a = component.props) === null || _a === void 0 ? void 0 : _a.multiple));
                    } }) })),
        React.createElement(SiteRouteAndParamPicker, { nearestOnly: true, component: component, routeId: assign.routeId, routeParam: assign.routeParam, siteRoutePickerProps: { label: 'Route' }, onChange: value => {
                updateRouteAndParam(value, 'props.assign.routeId', 'props.assign.routeParam').forEach(([path, value]) => update(path, value));
                update('props.assign.relations', undefined);
                update('props.assign.filterColumns', undefined);
            } }),
        React.createElement(ConfigFieldControl, null,
            React.createElement(Autocomplete, { fullWidth: true, options: entities, value: selectedEntity || null, getOptionLabel: option => option.displayName || option.name, onChange: (_, value) => {
                    update('props.assign.tableName', value === null || value === void 0 ? void 0 : value.tableName);
                    update('props.assign.relations', undefined);
                    update('props.assign.filterColumns', undefined);
                }, renderInput: params => (React.createElement(TextField, Object.assign({}, params, { fullWidth: true, size: 'small', label: 'Entity', placeholder: 'Select an Entity', helperText: 'Select Entity to update', InputProps: Object.assign(Object.assign({}, params.InputProps), { disableUnderline: true }) }))) })),
        properties.map((property, index) => {
            return (React.createElement(ConfigFieldControl, { key: `${property}-${index}` },
                React.createElement(StyledGrid, { container: true, spacing: 0 },
                    React.createElement(Grid, { item: true, className: 'grid-field' },
                        React.createElement(RelationPicker, { schema: property.schema, property: property.propertyName, helperText: index === 0 ? 'Select relation to be assigned' : undefined, 
                            // relationTarget is for filtering relations,
                            // only filter direct relation (index 0)
                            relationTarget: index === 0 ? relationTarget : undefined, onChange: relation => {
                                const propertyName = relation === null || relation === void 0 ? void 0 : relation.propertyName;
                                const copy = Array.from(assign.relations || []);
                                // unset all subsequent relations
                                if (!propertyName) {
                                    copy.splice(index);
                                }
                                else {
                                    copy[index] = propertyName || '';
                                }
                                update('props.assign.relations', copy);
                                update('props.assign.filterColumns', undefined);
                            } })),
                    index > 0 && (React.createElement(Grid, { item: true, className: 'grid-action' },
                        React.createElement(IconButton, { onClick: () => {
                                const copy = Array.from(assign.relations || []);
                                copy.splice(index);
                                update('props.assign.relations', copy);
                                update('props.assign.filterColumns', undefined);
                            } },
                            React.createElement(Close, null)))))));
        }),
        React.createElement(Box, { mt: 2 },
            React.createElement(Button, { fullWidth: true, size: 'small', variant: 'outlined', startIcon: React.createElement(Add, null), 
                // disabled if no selected for last property
                disabled: !properties[properties.length - 1].propertyName, onClick: () => {
                    if (!assign.relations) {
                        update('props.assign.relations', []);
                    }
                    insert('props.assign.relations', {});
                    update('props.assign.filterColumns', undefined);
                } }, "Add Relation")),
        React.createElement(ConfigFieldControl, null,
            React.createElement(Autocomplete, { multiple: true, fullWidth: true, options: dialogTableFields, value: assign.filterColumns || [], onChange: (_, options) => {
                    update('props.assign.filterColumns', options);
                }, renderInput: params => (React.createElement(TextField, Object.assign({}, params, { fullWidth: true, size: 'small', label: 'Filter Columns', helperText: 'Dialog table columns to filter when searching', InputProps: Object.assign(Object.assign({}, params.InputProps), { disableUnderline: true }) }))) })),
        React.createElement(Box, { mt: 1 },
            React.createElement(Typography, { variant: 'overline' }, "Match Selected Item(s)"),
            React.createElement(Typography, { variant: 'body1', color: 'textSecondary' },
                "Select query fields from ",
                React.createElement("strong", null, "dialog table"),
                " and",
                ' ',
                React.createElement("strong", null, "card table"),
                " that will be checked to determine if an item is ",
                React.createElement("strong", null, "selected"),
                ".")),
        React.createElement(ConfigFieldControl, null,
            React.createElement(Autocomplete, { fullWidth: true, options: dialogTableFields, value: ((_m = component.props) === null || _m === void 0 ? void 0 : _m.dialogTableField) || null, onChange: (_, field) => {
                    update('props.dialogTableField', field || undefined);
                }, renderInput: params => (React.createElement(TextField, Object.assign({}, params, { fullWidth: true, size: 'small', label: 'Dialog Table Field', InputProps: Object.assign(Object.assign({}, params.InputProps), { disableUnderline: true }) }))) })),
        React.createElement(ConfigFieldControl, null,
            React.createElement(Autocomplete, { fullWidth: true, options: cardTableFields, value: ((_o = component.props) === null || _o === void 0 ? void 0 : _o.cardTableField) || null, onChange: (_, field) => {
                    update('props.cardTableField', field || undefined);
                }, renderInput: params => (React.createElement(TextField, Object.assign({}, params, { fullWidth: true, size: 'small', label: 'Card Table Field', InputProps: Object.assign(Object.assign({}, params.InputProps), { disableUnderline: true }) }))) }))));
};
const RelationPicker = props => {
    const { property, schema, helperText, relationTarget, onChange } = props;
    const filteredRelations = useMemo(() => {
        const relations = (schema === null || schema === void 0 ? void 0 : schema.relations) || [];
        const relationTargets = Array.isArray(relationTarget)
            ? relationTarget
            : relationTarget
                ? [relationTarget]
                : [];
        if (relationTargets.length === 0) {
            return relations;
        }
        return relations.filter(relation => {
            return relationTargets.includes(relation.target);
        });
    }, [relationTarget, schema]);
    const selectedRelation = filteredRelations.find(relation => relation.propertyName === property);
    return (React.createElement(Autocomplete, { fullWidth: true, disabled: !schema, options: filteredRelations, value: selectedRelation || null, getOptionLabel: option => option.displayName, renderOption: option => {
            const joinColumn = typeof option.joinColumn === 'object' && option.joinColumn.name;
            const joinTable = typeof option.joinTable === 'object' && option.joinTable.name;
            const meta = [];
            joinTable && meta.push(joinTable);
            joinColumn && meta.push(joinColumn);
            const all = meta.join(', ');
            return (React.createElement(ListItemText, { primaryTypographyProps: { style: { display: 'flex' } }, primary: `${option.displayName} (${option.propertyName})`, secondary: option.type.concat(all && ` (${all})`) }));
        }, onChange: (_, relation) => onChange === null || onChange === void 0 ? void 0 : onChange(relation || undefined), renderInput: params => (React.createElement(TextField, Object.assign({}, params, { fullWidth: true, size: 'small', label: 'Relation', placeholder: 'Select Relation', helperText: helperText, InputProps: Object.assign(Object.assign({}, params.InputProps), { disableUnderline: true }) }))) }));
};
export const AssignEntityDialogPlugin = {
    name: 'Assign Entity Dialog',
    type: 'assign-entity-dialog',
    component: SiteAssignEntityDialog,
    configurator: AssignEntityDialogConfigurator,
    slots: [{ name: 'Table', type: 'table', slot: 'table' }]
};
//# sourceMappingURL=AssignEntityDialogPlugin.js.map